import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fas, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { RootState } from '../../store';
import ReportDataTable, {
  ReportColumn,
} from 'shared/components/Table/ReportDataTable';
import DrillBreadcrumb from 'shared/components/Dashboard/DrillBreadcrumb';
import { scopeLabel, withUdise } from 'shared/json/drilldown';
import {
  ALERT_TYPES,
  useGetSchoolAlertsQuery,
  useGetSchoolAlertsListQuery,
  useGetStudentChildCountAllYearQuery,
} from './Dashboard.service';

import './Caution.scss';

/** Render a Caution icon — supports PrimeIcons ("pi …") and FontAwesome. */
const CautionIcon: React.FC<{ icon: string; className?: string }> = ({
  icon,
  className = '',
}) => {
  if (icon.includes('fa-')) {
    const name = icon
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.startsWith('fa-'))
      .map((t) => t.replace(/^fa-/, ''))
      .filter((t) => !['solid', 'regular', 'brands', 'light'].includes(t))
      .pop();
    const def = name
      ? fas[
          `fa${name
            .split('-')
            .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
            .join('')}` as keyof typeof fas
        ]
      : undefined;
    if (def) return <FontAwesomeIcon icon={def} className={className} />;
  }
  return <i className={`${className} ${icon}`.trim()} />;
};

/** Classification filters shared from the Dashboard header (id strings). */
interface CautionFilters {
  management: string[];
  category: string[];
  schoolType: string[];
}

export interface CautionProps {
  /** Academic year id (required by the alerts API). */
  acdYr?: number | null;
  /** Selected geography ids + names from the header. */
  districtId?: number | null;
  blockId?: number | null;
  schoolId?: number | null;
  district?: string | null;
  /** Block name from the header (for the drill breadcrumb). */
  block?: string | null;
  /** School name from the header (for the drill breadcrumb). */
  school?: string | null;
  /** Management / Category / School-Type filter selections. */
  filters?: CautionFilters;
  /**
   * Clear/return to a level. Drive the breadcrumb's Back button and crumbs,
   * and are the same handlers the header dropdowns use so both stay in sync.
   */
  onDistrictChange?: (value: string) => void;
  onBlockChange?: (value: string) => void;
  onSchoolChange?: (value: string) => void;
  /**
   * Report the Block / School dropdown options the HEADER should show while
   * this caution tab is active: only the blocks/schools that actually have
   * caution records for the selected district. The dashboard overrides its
   * header dropdowns with these, and reverts to the normal full lists when the
   * tab unmounts (the component calls this with null on cleanup).
   */
  onScopeOptions?: (
    opts: {
      blocks: { label: string; value: string }[];
      schools: { label: string; value: string }[];
    } | null
  ) => void;
  /**
   * Focus the dashboard on one school. Rows in the flagged-school table are
   * already at school level, so clicking one cannot drill deeper — instead it
   * scopes the header (District → Block → School) to that school. All three
   * levels are sent together because the cascading header handlers each reset
   * their children, so setting them one at a time would wipe the block/school.
   */
  onGeoSelect?: (geo: {
    district?: string | null;
    block?: string | null;
    school?: string | null;
  }) => void;
}

const EMPTY_FILTERS: CautionFilters = {
  management: [],
  category: [],
  schoolType: [],
};

/** The five caution checks — alert_type int maps 1:1 to the backend. */
const CAUTION_CHECKS = [
  {
    alertType: 1,
    key: 'zero_enrolment',
    title: 'Zero Enrolment',
    icon: 'fa-solid fa-user-slash',
    severity: 'High' as const,
    meaning:
      'No students on roll — school may be non-functional or pending closure',
  },
  {
    alertType: 2,
    key: 'zero_teachers',
    title: 'Zero Teachers',
    icon: 'fa-solid fa-user-xmark',
    severity: 'High' as const,
    meaning: 'Students on roll but no teaching staff posted',
  },
  {
    alertType: 5,
    key: 'ptr_over_60',
    title: 'Pupil-Teacher Ratio Over 60',
    icon: 'pi pi-exclamation-triangle',
    severity: 'High' as const,
    meaning: 'Well past the 30:1 norm — severe teacher shortage',
  },
  {
    alertType: 4,
    key: 'single_teacher',
    title: 'Single-Teacher School',
    icon: 'fa-solid fa-user',
    severity: 'Medium' as const,
    meaning: 'Only one teacher for the whole school',
  },
  {
    alertType: 3,
    key: 'under_10_students',
    title: 'Under 10 Students',
    icon: 'fa-child fa-solid',
    severity: 'Medium' as const,
    meaning: 'Very low roll — viability and consolidation review',
  },
];

/** Coerce a value (may be a string via Convstr) to a number. */
const toNum = (v: unknown): number => {
  if (typeof v === 'number') return Number.isNaN(v) ? 0 : v;
  if (typeof v === 'string') {
    const n = Number(v.replace(/,/g, '').trim());
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
};

/**
 * The "Finding" text per alert type. The API doesn't return it, so we build it
 * from the row's total students / teachers (matching the alert's condition).
 * 1 zero_enrolment · 2 zero_teachers · 3 under_10_students · 4 single_teacher
 * · 5 ptr_over_60.
 */
const buildFinding = (
  alertType: number,
  students: number,
  teachers: number
): string => {
  switch (alertType) {
    case 1:
      return '0 students on roll';
    case 2:
      return `${students.toLocaleString('en-IN')} students, no teacher`;
    case 3:
      return `${students.toLocaleString('en-IN')} students on roll`;
    case 4:
      return `1 teacher for ${students.toLocaleString('en-IN')} students`;
    case 5: {
      const ptr = teachers > 0 ? Math.round(students / teachers) : 0;
      return `PTR ${ptr}:1`;
    }
    default:
      return '';
  }
};

/**
 * Caution view: hard-check tiles (counts from schoolalerts) + a detail table
 * for the selected check (rows from schoolalertslist). API data only, scoped
 * by the header academic year / geography / classification selection.
 */
const Caution: React.FC<CautionProps> = ({
  acdYr = null,
  districtId = null,
  blockId = null,
  // schoolId (header schoollist id) is intentionally NOT used: it's the wrong
  // id space for the alert endpoints. The school is scoped via resolvedSchoolId
  // (from studentchildcountallyear) instead. Prop kept for interface stability.
  district = null,
  block = null,
  school = null,
  filters = EMPTY_FILTERS,
  onDistrictChange,
  onBlockChange,
  onSchoolChange,
  onScopeOptions,
  // Clicking a flagged-school row jumps straight to that school; onGeoSelect
  // sets District + Block + School together (the row already carries its own
  // district/block names) so the header dropdowns land in sync with the pick
  // and the drill breadcrumb — which only renders once a district is set —
  // appears above the grid.
  onGeoSelect,
}) => {
  const [activeType, setActiveType] = useState(1);
  const [reportFirst, setReportFirst] = useState(0);

  // Schools of the selected district (studentchildcountallyear). Its
  // `school_id` is the id space the alert endpoints expect — NOT the header's
  // schoollist `schoolId` prop (a DIFFERENT space), which the backend ignored
  // so the cards kept showing block-level counts for a selected school. Scoped
  // by the selected district + academic year; skipped until a school is being
  // viewed (a block is selected).
  const { data: schoolChildCount } = useGetStudentChildCountAllYearQuery(
    { acd_yr: acdYr as number, district_id: districtId as number },
    { skip: acdYr == null || districtId == null || !school }
  );

  // Resolve the selected school's CORRECT id from studentchildcountallyear by
  // name (normalized AND stripped of a trailing " (udise)" suffix so a header
  // value like "APS … (33331101811)" still matches the bare API school_name).
  const resolvedSchoolId = useMemo<number | null>(() => {
    if (!school) return null;
    const norm = (s: string) =>
      String(s)
        .replace(/\s*\(\d[\d/]*\)\s*$/, '')
        .trim()
        .toLowerCase();
    const target = norm(school);
    const row = (schoolChildCount?.result ?? []).find(
      (sc) => norm(String(sc.school_name ?? '')) === target
    );
    const id = row ? Number(row.school_id ?? 0) : 0;
    return id > 0 ? id : null;
  }, [school, schoolChildCount]);

  // Shared filter body for both alert endpoints. Only sets keys that are
  // present; filter id strings → number arrays the endpoints expect.
  const baseArgs = useMemo(() => {
    const toIds = (ids: string[]) =>
      ids.map(Number).filter((n) => !Number.isNaN(n));
    const manage = toIds(filters.management);
    const cate = toIds(filters.category);
    const stype = toIds(filters.schoolType);
    // Scope to a school using the drill-resolved id (correct id space), not the
    // header's schoollist id prop. If a school is selected but not yet resolved
    // we stay at block scope for a tick until schoolChildCount arrives.
    const effSchoolId = resolvedSchoolId ?? null;
    return {
      acd_yr: acdYr as number,
      ...(districtId != null && { district_id: districtId }),
      ...(blockId != null && { block_id: blockId }),
      ...(effSchoolId != null && { school_id: effSchoolId }),
      ...(manage.length > 0 && { manage_id: manage }),
      ...(cate.length > 0 && { cate_id: cate }),
      ...(stype.length > 0 && { school_type_id: stype }),
    };
  }, [acdYr, districtId, blockId, resolvedSchoolId, filters]);

  // Card counts (schoolalerts). Skipped until a year is known.
  const { data: alerts } = useGetSchoolAlertsQuery(baseArgs, {
    skip: acdYr == null,
  });

  // count per alert_type — tolerant to object or array shapes from the API.
  const countByType = useMemo(() => {
    const map = new Map<number, number>();
    const result = alerts?.result;
    if (Array.isArray(result)) {
      result.forEach((r) => {
        const t = toNum(r.alert_type);
        if (t) map.set(t, toNum(r.count));
      });
    } else if (result && typeof result === 'object') {
      // Object keyed by alert name OR id → count.
      CAUTION_CHECKS.forEach((c) => {
        const rec = result as Record<string, number | string>;
        const v = rec[String(c.alertType)] ?? rec[c.key];
        if (v != null) map.set(c.alertType, toNum(v));
      });
    }
    return map;
  }, [alerts]);

  const totalFlagged = useMemo(
    () =>
      CAUTION_CHECKS.reduce(
        (s, c) => s + (countByType.get(c.alertType) ?? 0),
        0
      ),
    [countByType]
  );

  const selected =
    CAUTION_CHECKS.find((c) => c.alertType === activeType) ?? CAUTION_CHECKS[0];

  // Detail list for the active alert type (schoolalertslist).
  const { data: alertList, isFetching: alertListFetching } =
    useGetSchoolAlertsListQuery(
      { ...baseArgs, alert_type: activeType },
      { skip: acdYr == null }
    );

  const detailRows = useMemo(() => {
    const norm = (s: string) => s.trim().toLowerCase();
    let rows = (alertList?.result ?? []).map((r) => {
      // Backend row carries `total` (students) + `teach_tot` (teachers).
      const students = toNum(r.total);
      const teachers = toNum(r.teach_tot);
      const ptr = teachers > 0 ? students / teachers : 0;
      return {
        district: String(r.district_name ?? ''),
        block: String(r.block_name ?? ''),
        school: withUdise(
          String(r.school_name ?? ''),
          String(r.udise_code ?? '')
        ),
        // Bare name kept for matching the header's selected school (which has
        // no UDISE suffix) — the `school` display string does, so matching on
        // it wrongly filtered to zero rows ("No data found"). (#17)
        schoolRaw: String(r.school_name ?? ''),
        management: String(r.management ?? ''),
        students,
        teachers,
        ptr,
        // Keep the numeric ids for reliable client-side scoping (names from the
        // API vary in case/format, so id matching is the primary filter).
        districtRowId: Number(r.district_id ?? 0) || null,
        blockRowId: Number(r.block_id ?? 0) || null,
        // No per-row `finding` from the API — derive it from the alert type.
        finding: buildFinding(activeType, students, teachers),
      };
    });

    if (districtId != null || district) {
      rows = rows.filter(
        (row) =>
          (districtId != null && row.districtRowId === districtId) ||
          (!!district && norm(row.district) === norm(district))
      );
    }
    if (blockId != null || block) {
      rows = rows.filter(
        (row) =>
          (blockId != null && row.blockRowId === blockId) ||
          (!!block && norm(row.block) === norm(block))
      );
    }
    if (school) {
      // Match on the bare school name (header selection carries no UDISE).
      rows = rows.filter((row) => norm(row.schoolRaw) === norm(school));
    }

    switch (activeType) {
      case ALERT_TYPES.ptr_over_60:
        return rows.filter((row) => row.ptr > 0).sort((a, b) => b.ptr - a.ptr);
      case ALERT_TYPES.zero_enrolment:
        return rows.sort((a, b) => a.school.localeCompare(b.school));
      case ALERT_TYPES.under_10_students:
        return rows
          .filter((row) => row.students >= 1 && row.students <= 10)
          .sort((a, b) => b.students - a.students);
      default:
        // Keep EVERY row the server returned for this alert type (e.g.
        // single_teacher, zero_teachers) and only sort. The previous
        // `students > 0` filter dropped legitimately-flagged schools that have
        // 0 students on roll (a single-teacher school can have no enrolment),
        // so the table lost ~rows and its count no longer matched the server
        // summary / detail API. Sorting puts the highest rolls first; 0-student
        // rows sort to the end but are no longer discarded.
        return [...rows].sort((a, b) => b.students - a.students);
    }
  }, [alertList, activeType, districtId, blockId, district, block, school]);

  // ── Header dropdown scoping ──────────────────────────────────────────────
  // While a district is selected on this caution tab, the header's Block and
  // School dropdowns should list ONLY the blocks/schools that actually have
  // caution records (not every block/school in the district). We derive those
  // distinct lists from the caution rows and report them up; the dashboard
  // overrides its header dropdowns with them. On unmount (tab switch) we report
  // null so the header reverts to its normal full lists.
  const scopeBlocks = useMemo(() => {
    if (districtId == null) return [];
    const seen = new Set<string>();
    (alertList?.result ?? []).forEach((r) => {
      const nm = String(r.block_name ?? '').trim();
      if (nm) seen.add(nm);
    });
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b))
      .map((nm) => ({ label: nm, value: nm }));
  }, [alertList, districtId]);

  const scopeSchools = useMemo(() => {
    if (districtId == null) return [];
    const seen = new Set<string>();
    (alertList?.result ?? []).forEach((r) => {
      const nm = String(r.school_name ?? '').trim();
      if (nm) seen.add(nm);
    });
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b))
      .map((nm) => ({ label: nm, value: nm }));
  }, [alertList, districtId]);

  useEffect(() => {
    // Only override when a district is selected; otherwise let the header use
    // its normal lists.
    if (districtId == null) {
      onScopeOptions?.(null);
    } else {
      onScopeOptions?.({ blocks: scopeBlocks, schools: scopeSchools });
    }
    // Revert on unmount (leaving the caution tab).
    return () => onScopeOptions?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtId, scopeBlocks, scopeSchools]);

  // Switching the active check (e.g. Zero Enrolment -> Zero Teachers) keeps
  // the header's school selection, but the new check's flagged-school list
  // may not include that school at all — the table then got stuck on a
  // dangling selection showing no rows, with the dropdown looking
  // unselected. Once this check's own list has actually loaded, auto-clear
  // the selection if it isn't in there, falling back to the current
  // block's full flagged-school list instead of staying empty.
  const lastClearedCheck = React.useRef<number | null>(null);
  useEffect(() => {
    // Auto-drop a carried-over school that doesn't apply here — but ONLY when
    // the active CHECK just changed (or on first load), NOT on a deliberate
    // school pick. The header's School dropdown falls back to the block's
    // full school list when this check has no flagged schools, so a user can
    // pick any school to inspect it; if that school has no record for the
    // current check the list simply shows "No records found" and the
    // selection must PERSIST rather than bounce straight back to unselected.
    // Gating on activeType draws that line: a check switch re-evaluates and
    // clears a now-irrelevant carried school; a plain school pick (same
    // check) does not.
    if (!school || alertListFetching || !alertList) return;
    if (lastClearedCheck.current === activeType) return;
    lastClearedCheck.current = activeType;
    const rows = alertList.result ?? [];
    const norm = (s: string) => s.trim().toLowerCase();
    const stillPresent = rows.some(
      (r) => norm(String(r.school_name ?? '')) === norm(school)
    );
    if (!stillPresent) {
      onSchoolChange?.('');
    }
  }, [school, activeType, alertList, alertListFetching, onSchoolChange]);

  // Rows for the "all checks" summary table.
  const checkRows = useMemo(
    () =>
      CAUTION_CHECKS.map((c) => ({
        alertType: c.alertType,
        title: c.title,
        severity: c.severity,
        // Always the backend summary count (schoolalerts) — the same stable
        // value the cards show, for every check including the active one. It
        // must NOT follow the rendered grid (detailRows), which is scoped and
        // would make the selected row's count disagree with the backend.
        count: countByType.get(c.alertType) ?? 0,
        meaning: c.meaning,
        // Leading icon for the Check column in the summary table.
        icon: c.icon,
      })),
    [countByType]
  );

  const detailColumns: ReportColumn[] = [
    { key: 'district', label: 'District', type: 'text' },
    { key: 'block', label: 'Block', type: 'text' },
    // Schools are the LEAF of the drill (district → block → school), so this
    // column is plain text — no clickable arrow promising a deeper level.
    // The UDISE is appended into the name, so there's no separate column.
    // Clickable → opens the flagged school's detail view.
    { key: 'school', label: 'School', type: 'link' },
    { key: 'management', label: 'Management', type: 'muted' },
    { key: 'students', label: 'Students', type: 'number' },
    { key: 'finding', label: 'Finding', type: 'finding' },
  ];

  const checksTableColumns: ReportColumn[] = [
    { key: 'title', label: 'Check', type: 'link', sortable: false },
    { key: 'severity', label: 'Severity', type: 'badge', sortable: false },
    {
      key: 'count',
      label: 'Schools',
      type: 'number',
      bold: true,
      sortable: false,
    },
    { key: 'meaning', label: 'What it means', type: 'muted', sortable: false },
  ];

  // Deepest selected level, not just the district — a heading must not say
  // "Chennai" when the figures are scoped to one block or school within it.
  const scopeName = scopeLabel({ district, block, school });

  return (
    <div className="caution">
      {/* ── Header ── */}
      <div className="caution__head dash-section">
        <h5 className="caution__title">
          Caution — {scopeName}
          <span className="caution__subtitle">
            {' '}
            {totalFlagged.toLocaleString('en-IN')} schools flagged
          </span>
        </h5>
      </div>

      {/* ── Info banner ── */}
      <div className="dash-section">
        <div className="caution__banner">
          <i className="pi pi-exclamation-circle" />
          <span>
            Schools failing a hard check — a facility, a figure or a submission
            that is zero — rather than merely scoring below average. Every list
            below downloads in full, school by school.
          </span>
        </div>
      </div>

      {/* ── Check tiles ── */}
      <div className="row g-3 dash-section">
        {CAUTION_CHECKS.map((c) => (
          <div className="col-6 col-md-4 col-xl-3" key={c.key}>
            <button
              type="button"
              className={`caution__tile caution__tile--${c.severity.toLowerCase()}${
                activeType === c.alertType ? ' is-active' : ''
              }`}
              onClick={() => setActiveType(c.alertType)}
            >
              <div className="caution__tile-top">
                <CautionIcon icon={c.icon} className="caution__tile-icon" />
                <span className="caution__tile-count">
                  {/* Always the backend summary count (schoolalerts), whether
                      or not this card is active. The count must stay stable and
                      match the API — it must NOT follow the rendered table
                      (detailRows), which is scoped/sorted and would make the
                      active card's number drift from the backend. */}
                  {(countByType.get(c.alertType) ?? 0).toLocaleString('en-IN')}
                </span>
              </div>
              <span className="caution__tile-title">{c.title}</span>
              <span className="caution__tile-desc">{c.meaning}</span>
            </button>
          </div>
        ))}
      </div>

      {/* ── Detail table for the selected check ── */}
      <div className="dash-section">
        {/* key per alert type → React remounts a fresh table on tab switch,
            discarding PrimeReact's retained sort / filter / pagination state
            (which otherwise made the per-check filter stop applying after the
            first selection). */}
        <DrillBreadcrumb
          district={district}
          block={block}
          school={school}
          onDistrictChange={onDistrictChange}
          onBlockChange={onBlockChange}
          onSchoolChange={onSchoolChange}
        />
        <ReportDataTable
          key={selected.key}
          title={selected.title}
          hint={selected.meaning}
          icon={selected.icon}
          data={detailRows as unknown as Record<string, unknown>[]}
          columns={detailColumns}
          dataKey="school"
          exportFileName={`caution-${selected.key}`}
          searchPlaceholder="Search school, district, block or UDISE..."
          rows={12}
          // Rows ARE schools (the leaf) — clicking one jumps straight to that
          // school, syncing the header District/Block/School dropdowns too
          // (the row already carries its own district/block names) so the
          // breadcrumb above this grid appears and matches the selection.
          onRowClick={(row) =>
            onGeoSelect?.({
              district: String(row.district ?? '') || null,
              block: String(row.block ?? '') || null,
              school: String(row.schoolRaw ?? row.school ?? '') || null,
            })
          }
          first={reportFirst}
          onPage={setReportFirst}
        />
      </div>

      {/* ── All checks summary table ── */}
      <div className="dash-section cautions-grid">
        <ReportDataTable
          title={`All Caution Checks — ${scopeName}`}
          hint="counts across every check"
          icon="fa-solid fa-table-list"
          data={checkRows as unknown as Record<string, unknown>[]}
          columns={checksTableColumns}
          exportFileName="all-caution-checks"
          searchable={false}
          rows={12}
          onRowClick={(row) => setActiveType(Number(row.alertType))}
          isRowActive={(row) => Number(row.alertType) === activeType}
        />
      </div>
    </div>
  );
};

export default Caution;
